import { Bell, ExternalLink, Eye, EyeOff, FileText, History, KeyRound, LayoutDashboard, LogOut, Menu, Newspaper, Save, Trophy, Users, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { useAuth } from "../../context/AuthContext";
import { useAdminToast } from "../../context/AdminToastContext";
import { adminErrorMessage, missingFieldsMessage } from "../../lib/adminFeedback";
import { writeAuditLog } from "../../lib/audit";
import { adminRoleLabel, hasAdminPermission, type AdminPermission } from "../../lib/adminPermissions";

const adminNavigation: Array<{ label: string; to: string; icon: typeof LayoutDashboard; end?: boolean; permission: AdminPermission }> = [
  { label: "Übersicht", to: "/admin", icon: LayoutDashboard, end: true, permission: "dashboard:view" },
  { label: "News", to: "/admin/news", icon: Newspaper, permission: "news:manage" },
  { label: "Seiteninhalte", to: "/admin/inhalte", icon: FileText, permission: "content:manage" },
  { label: "Mannschaften", to: "/admin/mannschaften", icon: Trophy, permission: "content:manage" },
  { label: "Admin-Team", to: "/admin/team", icon: Users, permission: "users:manage" },
  { label: "Historie", to: "/admin/historie", icon: History, permission: "history:view" },
];

const emptyPasswordForm = () => ({ currentPassword: "", newPassword: "", confirmation: "" });

export function AdminShell() {
  const { user, logout, changePassword, adminRole } = useAuth();
  const { showToast } = useAdminToast();
  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const location = useLocation();
  const navigation = adminNavigation.filter((item) => hasAdminPermission(adminRole, item.permission));
  const initials = (user?.email || "A").slice(0, 2).toUpperCase();
  const currentPage = [...navigation].reverse().find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to));

  const handleLogout = async () => {
    try {
      await logout();
      showToast({ type: "success", title: "Erfolgreich abgemeldet", message: "Deine Admin-Sitzung wurde sicher beendet." });
    } catch (error) {
      showToast({ type: "error", title: "Abmeldung fehlgeschlagen", message: adminErrorMessage(error, "Bitte versuche die Abmeldung erneut.") });
    }
  };

  const closePasswordModal = () => {
    if (passwordBusy) return;
    setPasswordOpen(false);
    setShowPasswords(false);
    setPasswordForm(emptyPasswordForm());
  };

  const submitPasswordChange = async (event: FormEvent) => {
    event.preventDefault();
    const missing = [
      !passwordForm.currentPassword && "aktuelles Passwort",
      !passwordForm.newPassword && "neues Passwort",
      !passwordForm.confirmation && "Passwort-Wiederholung",
    ].filter((field): field is string => Boolean(field));
    if (missing.length > 0) {
      showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      showToast({ type: "warning", title: "Passwort zu kurz", message: "Das neue Passwort muss mindestens 8 Zeichen enthalten." });
      return;
    }
    if (passwordForm.newPassword === passwordForm.currentPassword) {
      showToast({ type: "warning", title: "Neues Passwort erforderlich", message: "Das neue Passwort muss sich vom aktuellen Passwort unterscheiden." });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmation) {
      showToast({ type: "warning", title: "Passwörter stimmen nicht überein", message: "Bitte wiederhole das neue Passwort identisch." });
      return;
    }

    setPasswordBusy(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      await writeAuditLog({ action: "account.password_changed", entityType: "account", entityId: user?.id, entityTitle: user?.email || "Eigenes Konto" });
      setPasswordOpen(false);
      setShowPasswords(false);
      setPasswordForm(emptyPasswordForm());
      showToast({ type: "success", title: "Passwort geändert", message: "Dein neues Passwort ist ab sofort aktiv." });
    } catch (error) {
      showToast({ type: "error", title: "Passwort nicht geändert", message: adminErrorMessage(error, "Das Passwort konnte nicht geändert werden.") });
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className="admin-shell">
      {open && <button type="button" className="admin-sidebar-backdrop" onClick={() => setOpen(false)} aria-label="Navigation schließen" />}
      <aside className={`admin-sidebar ${open ? "admin-sidebar--open" : ""}`}>
        <div className="admin-sidebar-top"><Brand /><button type="button" onClick={() => setOpen(false)} aria-label="Navigation schließen"><X /></button></div>
        <div className="admin-context"><span>Adminbereich</span><strong>Vereinsverwaltung</strong></div>
        <nav aria-label="Admin-Navigation">{navigation.map((item) => <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)}>{({ isActive }) => <><span className={isActive ? "active" : ""}><item.icon /></span>{item.label}</>}</NavLink>)}</nav>
        <div className="admin-sidebar-bottom"><a href="/" target="_blank" rel="noreferrer">Website ansehen <ExternalLink /></a><button type="button" onClick={() => { setOpen(false); setPasswordOpen(true); }}><KeyRound /> Passwort ändern</button><button type="button" onClick={() => void handleLogout()}><LogOut /> Abmelden</button></div>
      </aside>
      <div className="admin-main">
        <header className="admin-topbar">
          <button type="button" className="admin-menu" onClick={() => setOpen(true)} aria-label="Navigation öffnen"><Menu /></button>
          <div className="admin-current-page"><small>Vereinsverwaltung</small><strong>{currentPage?.label || "Adminbereich"}</strong></div>
          <div className="admin-topbar-spacer" />
          <button type="button" className="notification-button" aria-label="Rückmeldungen" onClick={() => showToast({ type: "info", title: "Rückmeldungen sind aktiv", message: "Speichern, Uploads und Fehler werden dir hier direkt angezeigt." })}><Bell /></button>
          <button type="button" className="admin-user" onClick={() => setPasswordOpen(true)} aria-label="Eigenes Passwort ändern"><span>{initials}</span><div><strong>{user?.user_metadata.full_name || "Admin-Team"}</strong><small>{adminRoleLabel(adminRole)} · {user?.email}</small></div></button>
        </header>
        <div className="admin-content"><Outlet /></div>
      </div>
      {passwordOpen && <div className="editor-overlay" role="dialog" aria-modal="true" aria-labelledby="password-dialog-title"><div className="invite-modal password-modal"><div className="editor-header"><div><span>Eigenes Konto</span><h2 id="password-dialog-title">Passwort ändern</h2></div><button type="button" onClick={closePasswordModal} aria-label="Fenster schließen"><X /></button></div><form onSubmit={submitPasswordChange} noValidate><span className="invite-icon"><KeyRound /></span><p>Bestätige zuerst dein aktuelles Passwort. Das neue Passwort gilt direkt bei der nächsten Anmeldung.</p><div className="password-account"><span>Angemeldetes Konto</span><strong>{user?.email}</strong></div><label>Aktuelles Passwort<span className="password-input"><input type={showPasswords ? "text" : "password"} value={passwordForm.currentPassword} onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })} autoComplete="current-password" /><button type="button" onClick={() => setShowPasswords((current) => !current)} aria-label={showPasswords ? "Passwörter ausblenden" : "Passwörter anzeigen"}>{showPasswords ? <EyeOff /> : <Eye />}</button></span></label><label>Neues Passwort<input type={showPasswords ? "text" : "password"} value={passwordForm.newPassword} onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })} placeholder="Mindestens 8 Zeichen" autoComplete="new-password" /></label><label>Neues Passwort wiederholen<input type={showPasswords ? "text" : "password"} value={passwordForm.confirmation} onChange={(event) => setPasswordForm({ ...passwordForm, confirmation: event.target.value })} autoComplete="new-password" /></label><div className="invite-actions"><button type="button" className="admin-secondary-button" onClick={closePasswordModal} disabled={passwordBusy}>Abbrechen</button><button className="admin-primary-button" disabled={passwordBusy}><Save /> {passwordBusy ? "Wird geändert…" : "Passwort speichern"}</button></div></form></div></div>}
    </div>
  );
}
